/**
 * Thread Add-People — pure create-then-add sequencing (group-threading P2c).
 *
 * Adding a participant is two authorized server calls: (1) ensure the membership
 * doc exists (`create` — idempotent; an existing doc resolves `already_exists`,
 * still ok) so the actor is recorded as creator when the thread was single-user,
 * then (2) `add` the target. Extracted as a pure function over injected callers so
 * the ordering + short-circuit are unit-tested without React or the network.
 *
 * Fail-closed: if `create` does not succeed we do NOT attempt `add` (never add into
 * a thread whose membership doc we couldn't establish). The server remains the sole
 * authority — this only sequences the two calls and reports which step decided.
 */
import type { MembershipWriteOutcome } from '../../repositories/threadMembershipWriteClient';

export interface AddParticipantFlowDeps {
  /** Ensure the membership doc exists (P2a `createThreadMembership`). */
  ensureThread: (threadId: string) => Promise<MembershipWriteOutcome>;
  /** Add the target participant (P2a `addThreadParticipant`). */
  addParticipant: (threadId: string, targetId: string) => Promise<MembershipWriteOutcome>;
}

export interface AddParticipantFlowResult {
  ok: boolean;
  /** The server (or client sentinel) decision that determined the outcome. */
  decision: string;
  /** Which step decided: `create` when the ensure step failed, else `add`. */
  step: 'create' | 'add' | 'input';
  threadId: string;
  targetId: string;
}

/**
 * Run the create-then-add flow. Short-circuits on blank input and on a failed
 * ensure step (no `add` attempted). Returns the deciding step + decision.
 */
export async function runAddParticipant(
  deps: AddParticipantFlowDeps,
  threadIdRaw: string,
  targetIdRaw: string,
): Promise<AddParticipantFlowResult> {
  const threadId = (threadIdRaw ?? '').trim();
  const targetId = (targetIdRaw ?? '').trim();

  if (!threadId || !targetId) {
    return { ok: false, decision: 'client_invalid_input', step: 'input', threadId, targetId };
  }

  const created = await deps.ensureThread(threadId);
  if (!created.ok) {
    // Fail-closed: never add into a thread whose membership doc wasn't established.
    return { ok: false, decision: created.decision, step: 'create', threadId, targetId };
  }

  const added = await deps.addParticipant(threadId, targetId);
  return { ok: added.ok, decision: added.decision, step: 'add', threadId, targetId };
}

/**
 * Map a failed flow result to user-facing copy. Keyed on the real server decision
 * codes (`threadMembershipWrite`) plus the client sentinels; falls back to a
 * step-appropriate generic. Copy only — never authorizes.
 */
export function describeAddParticipantFailure(
  res: Pick<AddParticipantFlowResult, 'decision' | 'step'>,
): string {
  switch (res.decision) {
    case 'already_participant':
      return "They're already in this thread.";
    case 'not_admittable_fail_closed':
      // Reachable when someone was invited but hasn't accepted, or when their
      // membership was suspended. Naming the next step beats a flat refusal.
      return "They aren't an active member of this workspace yet — check Team & Roles (they may not have accepted their invitation).";
    case 'account_boundary_fail_closed':
      return 'That person belongs to a different workspace, so they can’t be added here.';
    case 'actor_not_member_fail_closed':
    case 'invite_not_allowed_fail_closed':
      return "You don't have permission to add people here.";
    case 'thread_not_found_fail_closed':
      return 'This thread is no longer available.';
    case 'invalid_target_fail_closed':
    case 'client_invalid_input':
      return 'Pick someone to add.';
    case 'unauthenticated_fail_closed':
    case 'client_no_auth':
    case 'client_no_token':
      return 'Please sign in again.';
    case 'client_network_error':
      return 'Network problem — try again.';
    default:
      return res.step === 'create' ? "Couldn't start the group here." : "Couldn't add that person.";
  }
}
